import * as Sentry from '@sentry/react';
import webSDKCore from '@tencent/ti18n-websdkcore';
import type { SeverityLevel } from '@sentry/react';
import { getTextConfig } from '../env';

export const setExtras = (extras: any) => {
  Sentry.setExtras(extras);
};

export const captureMessage = <T>(title: string, extra?: T, level: SeverityLevel = 'warning') => {
  Sentry.captureMessage(title, {
    extra: extra || {},
    level,
  });
};

export const captureEvent = (event: any) => {
  Sentry.captureEvent(event);
};

export const captureException = (error: Error | string) => {
  let err;
  if (error instanceof Error) {
    err = error;
  } else {
    err = new Error(error);
  }
  Sentry.captureException(err);
};

export const getWaterMarker = (str: string, type?: string) => {
  let cpyName = str;
  if (str.length > 16) {
    cpyName = str.substring(0, 16);
  }
  const can = document.createElement('canvas');
  can.width = 180;
  can.height = 110;
  can.style.display = 'none';
  can.style.zIndex = '999';
  const cans = can.getContext('2d');
  if (cans) {
    cans.rotate(-25 * Math.PI / 180);
    cans.font = `400 ${type === 'mobile' ? '20px' : '30px'} Microsoft JhengHei`;
    cans.fillStyle = '#999';
    cans.textAlign = 'center';
    cans.textBaseline = 'middle';
    if (cans.measureText(cpyName).width > 180) {
      const size = 180 / cpyName.length;
      cans.font = `400 ${size}px Microsoft JhengHei`;
    }
    cans.fillText(cpyName, 60, 100);
  }
  return `url(${can.toDataURL('image/png')})`;
};

export const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

export const getOS = () => {
  const { userAgent } = window.navigator;
  if (userAgent.indexOf('Win') !== -1) {
    return 'Windows';
  } if (userAgent.indexOf('Mac') !== -1) {
    return 'Mac';
  }
  return 'Other';
};

export const getModifierKey = () => {
  const os = getOS();
  let modifierKey = 'Control';
  if (os === 'Windows') {
    modifierKey = 'Control';
  } else if (os === 'Mac') {
    modifierKey = 'Meta';
  }
  return modifierKey;
};

export const getSizeFormat = (size: number) => {
  if (size >= 1024 * 1024 * 1024) {
    return `${Math.round(size / 1024 / 1024 / 1024 * 100) / 100}GB`;
  } if (size >= 1024 * 1024) {
    return `${Math.round(size / 1024 / 1024 * 100) / 100}MB`;
  }
  return `${Math.round(size / 1024 * 100) / 100}KB`;
};

export const getProgress = (offset: number, size: number) => (Math.round(offset / size * 10000) / 100);

export const getSpeed = (receivedSizeDiff: number) => (Math.round(receivedSizeDiff / 1024 / 1024 * 100) / 100);

export const captureNetWorkException = (res: any = {}, options: any = {}) => {
  let msg;
  if (typeof res === 'string') {
    msg = res;
  } else {
    msg = res?.errMsg || res?.message;
  }
  console.error(msg, options);
};

export const changeLang = (lang: string) => {
  webSDKCore.changeLanguage(lang);
  document.title = webSDKCore.t(getTextConfig().appTitle);
  document.documentElement.lang = lang;
  const isPTeam = document.body.classList.contains('pteam');
  document.body.className = '';
  document.body.classList.add(lang);
  if (isPTeam) {
    document.body.classList.add('pteam');
  }
};

export const changeVersion = (bodyClass: string) => {
  if (bodyClass) {
    document.body.classList.add(bodyClass);
  } else {
    document.body.classList.remove('pteam');
  }
};

type RTCStatsType =
  'receiver' |  // 接收者信息
  'sender' |  // 发送者信息
  'transport' |  // 传输相关信息
  'peerConnection' |  // RTC连接信息
  'dataChannel' |  // 数据通道信息
  'certificate' |  // 证书信息
  'candidatePair' |  // ICE候选者
  'remoteCandidate' |  // ICE远程候选者
  'localCandidate' |  // ICE本地候选
  'csrc' |  // 下行流中共享源
  'mediaSource' |  // 媒体源
  'stream' |  // 媒体流
  'track' |  // 媒体轨道
  'codec' |  // 编解码器
  'inboundRtp' |  // RTC下行流量
  'outboundRtp' |  // RTC上行流量
  'remoteInboundRtp' |  // 远端RTC下行流量
  'remoteOutboundRtp';  // 远端RTC上行流量

export const getRTCStatsReports = (stats: RTCStatsReport, RTCStatsType: RTCStatsType) => {
  const statsTypeList: any[] = [];
  const statsType = RTCStatsType.replace(/([A-Z])/g, '-$1').toLowerCase();
  stats?.forEach((report: any) => {
    if (report.type === statsType) {
      statsTypeList.push(report);
    }
  });
  return statsTypeList;
};

export const getAudioLatency = (queueSize: number) => (Math.ceil(queueSize * 21.33 * 100) / 100);
